import React, { useState, useEffect, useRef } from "react";
import { MapPin, LocateFixed, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const GEO = `${process.env.REACT_APP_BACKEND_URL}/api/geo`;

export default function LocationInput({ value, onSelect, placeholder, testId, allowLocate = false }) {
  const [query, setQuery] = useState(value?.name || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const search = (q) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (q.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await fetch(`${GEO}/search?q=${encodeURIComponent(q)}`);
        const data = await r.json();
        setSuggestions(Array.isArray(data) ? data : []);
        setOpen(true);
      } catch (e) {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 400);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported on this device");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await fetch(`${GEO}/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`);
          const loc = await r.json();
          setQuery(loc.name);
          onSelect(loc);
          setOpen(false);
        } catch (e) {
          toast.error("Could not resolve your location");
        } finally {
          setLocating(false);
        }
      },
      () => {
        setLocating(false);
        toast.error("Location permission denied");
      },
      { timeout: 10000 }
    );
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex items-center">
        <input
          data-testid={testId}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onSelect(null);
            search(e.target.value);
          }}
          onFocus={() => suggestions.length && setOpen(true)}
          placeholder={placeholder}
          className="w-full bg-transparent outline-none text-base font-medium text-slate-900 placeholder:text-slate-400 placeholder:font-normal"
          autoComplete="off"
        />
        {loading && <Loader2 className="w-4 h-4 animate-spin text-amber-500 shrink-0" />}
        {!loading && query && (
          <button
            type="button"
            data-testid={`${testId}-clear`}
            onClick={() => { setQuery(""); onSelect(null); setSuggestions([]); }}
            className="p-1 text-slate-400 hover:text-slate-600"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        {allowLocate && (
          <button
            type="button"
            data-testid={`${testId}-gps`}
            onClick={useMyLocation}
            title="Use my current location"
            className="ml-1 p-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors shrink-0"
          >
            {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />}
          </button>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <div
          data-testid={`${testId}-suggestions`}
          className="absolute left-0 right-0 top-full mt-2 z-50 bg-white border border-slate-200 rounded-xl shadow-[0_12px_24px_-4px_rgba(15,23,42,0.12)] overflow-hidden max-h-60 overflow-y-auto"
        >
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              data-testid={`${testId}-suggestion-${i}`}
              onClick={async () => {
                setQuery(s.name);
                setOpen(false);
                if (s.lat != null) { onSelect(s); return; }
                try {
                  const r = await fetch(`${GEO}/place?place_id=${encodeURIComponent(s.place_id)}`);
                  const loc = await r.json();
                  onSelect(loc);
                } catch (e) {
                  onSelect(null);
                  toast.error("Could not resolve that place");
                }
              }}
              className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-amber-50 transition-colors border-b border-slate-50 last:border-0"
            >
              <MapPin className="w-4 h-4 text-amber-500 shrink-0" />
              <span className="text-sm font-medium text-slate-800 truncate">{s.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Haversine distance in km (straight-line; road distance ≈ 1.3x applied by caller if needed)
export function haversineKm(a, b) {
  if (!a || !b) return null;
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}
